import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Box, ButtonBase, Typography } from '@mui/material';
import { ArrowUpRight, FileText, FolderOpen } from 'lucide-react';
import { portalColors, portalFocus, portalLogo, portalMotion } from '../../portal-styles';
import { useIntl } from 'react-intl';
import * as componentActions from '../../../../redux/actions/organizationcategoriesportal';
import Api from '../../../../services/api';

export function CategoryRow({ element, image, onClick, onImageError, variant = 'category' }) {
  const intl = useIntl();
  const isService = variant === 'service';
  const fullName = (element.parentname || '').trim();
  const showFullName = fullName && fullName !== (element.name || '').trim();

  return (
    <ButtonBase onClick={onClick} focusRipple sx={{
      ...portalFocus, ...portalMotion, width: '100%', minWidth: 0, height: '100%', minHeight: isService ? 100 : 112,
      p: { xs: 1.5, sm: 2 }, display: 'grid', alignItems: 'center', gap: 2,
      gridTemplateColumns: { xs: isService ? 'auto minmax(0, 1fr)' : 'auto minmax(0, 1fr) 20px', sm: 'auto minmax(0, 1fr) auto' },
      textAlign: 'left', color: portalColors.ink, bgcolor: 'transparent', borderRadius: '16px',
      transition: 'background-color 180ms ease-out',
      '&:hover': { bgcolor: portalColors.soft,
        '& .portal-category-icon': { bgcolor: image ? portalColors.paper : portalColors.light, color: portalColors.action },
        '& .portal-category-arrow': { transform: 'translate(2px, -2px)', color: portalColors.action },
        '& .portal-service-action': { bgcolor: portalColors.action, color: '#fff' },
      },
    }}>
      <Box component="span" className="portal-category-icon" sx={{
        width: image ? { xs: 96, sm: 128 } : { xs: 40, sm: 44 },
        height: image ? { xs: 64, sm: 80 } : { xs: 40, sm: 44 },
        p: image ? 0.5 : 0, boxSizing: 'border-box', borderRadius: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: portalColors.action, bgcolor: image ? portalColors.paper : portalColors.light,
        overflow: 'hidden', transition: 'background-color 180ms ease-out, color 180ms ease-out',
      }}>
        {image ? <Box component="img" src={image} alt="" onError={onImageError}
          sx={portalLogo} />
          : isService ? <FileText size={21} strokeWidth={1.5} aria-hidden="true" />
            : <FolderOpen size={21} strokeWidth={1.5} aria-hidden="true" />}
      </Box>
      <Box component="span" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography component="span" sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, overflowWrap: 'anywhere', color: portalColors.ink }}>
          {element.name}
        </Typography>
        {showFullName && <Typography component="span" variant="body2" sx={{ color: portalColors.muted, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
          {fullName}
        </Typography>}
      </Box>
      {isService ? <Box component="span" className="portal-service-action" sx={{
        gridColumn: { xs: 2, sm: 3 }, justifySelf: { xs: 'start', sm: 'end' },
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1,
        minHeight: 44, px: 2, py: 1, borderRadius: '999px', bgcolor: portalColors.soft, color: portalColors.action,
        transition: 'background-color 180ms ease-out, color 180ms ease-out', maxWidth: '100%',
      }}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4, overflowWrap: 'anywhere', color: 'inherit' }}>
          {intl.formatMessage({ id: 'portal.category.access' })}
        </Typography>
        <ArrowUpRight size={18} strokeWidth={1.7} style={{ flexShrink: 0 }} aria-hidden="true" />
      </Box> : <Box component="span" className="portal-category-arrow" sx={{ display: 'flex', color: portalColors.action, transition: 'transform 180ms ease-out, color 180ms ease-out' }}>
        <ArrowUpRight size={20} strokeWidth={1.5} aria-hidden="true" />
      </Box>}
    </ButtonBase>
  );
}

function CardCategories({ element, addCurrent, history, match, variant }) {
  const [image, setImage] = React.useState('');

  React.useEffect(() => {
    let active = true;
    setImage('');
    if (element.attachmentid) {
      Api.post('/organizationcategories/attachments', { AttachmentId: element.attachmentid })
        .then(result => {
          if (active && result.data && result.data.data) {
            const value = result.data.data;
            setImage(value);
          }
        })
        .catch(() => {});
    }
    return () => { active = false; };
  }, [element.attachmentid]);

  const openCategory = () => {
    addCurrent(element);
    history.push({
      pathname: `/${match.params.lang}/portal-category/${element.id}`,
      state: { action: 'cardReload', parentId: element.parentorganizationcategoryid },
    });
  };

  return <CategoryRow element={element} image={image} onClick={openCategory} onImageError={() => setImage('')} variant={variant} />;
}

const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);
export default withRouter(connect(null, mapDispatchToProps)(CardCategories));
